import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";

export interface MultiOption {
  value: string;
  label: string;
  /** Second line, for anything that disambiguates two same-named options. */
  hint?: string;
}

/**
 * A filter that takes several answers, or none.
 *
 * Not the `Select` from `components/ui`: that one is single-valued and closes on
 * every pick, so choosing three grades means opening it three times. And not
 * Radix's `DropdownMenu` with checkbox items either — it owns typing for its own
 * typeahead, and the skill list is 186 options long, where a search box is the
 * only way in.
 *
 * Empty selection means *no filter*, never "match nothing". That is the whole
 * contract of these filters and it is also what the server does with an empty
 * list (`theoryOverview`), so clearing the last chip widens the view instead of
 * blanking it.
 */
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  className,
  searchable,
  allLabel = "Visi",
  emptyLabel = "Nėra ką pasirinkti",
}: {
  label: string;
  options: MultiOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  className?: string;
  /** Adds a search box. Worth it past a dozen options; noise below that. */
  searchable?: boolean;
  allLabel?: string;
  emptyLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);

  // Every ordinary way of saying "done" closes it, the same rule the figure
  // lightbox follows: a click anywhere outside, and Esc.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().includes(needle) ||
        option.hint?.toLowerCase().includes(needle),
    );
  }, [options, query]);

  // Selected values that are no longer on offer still have to be countable, or
  // the button would say „Visi" while a filter is quietly applied.
  const chosen = options.filter((option) => selected.includes(option.value));
  const summary =
    selected.length === 0
      ? allLabel
      : chosen.length === 1
        ? chosen[0].label
        : `${selected.length} pasirinkta`;

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  return (
    <div ref={root} className={`relative ${className || ""}`}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((prev) => !prev)}
        className="mt-1 h-9 w-full justify-between font-normal"
      >
        <span className={`truncate ${selected.length ? "" : "text-muted-foreground"}`}>
          {summary}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected.length > 0 && (
            <span
              role="button"
              tabIndex={-1}
              title="Išvalyti"
              onClick={(event) => {
                event.stopPropagation();
                onChange([]);
              }}
              className="rounded p-0.5 hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </span>
      </Button>

      {open && (
        <div className="absolute z-50 mt-1 w-[min(22rem,90vw)] min-w-full rounded-md border bg-popover p-1 shadow-md">
          {searchable && (
            <div className="flex items-center gap-2 border-b px-2 pb-1.5 pt-1">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Ieškoti..."
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          )}

          <div className="max-h-72 overflow-y-auto py-1">
            {shown.length === 0 && (
              <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                {options.length === 0 ? emptyLabel : "Nieko nerasta"}
              </div>
            )}
            {shown.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                      isSelected ? "bg-primary border-primary text-primary-foreground" : ""
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate">{option.label}</span>
                    {option.hint && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {option.hint}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {selected.length > 0 && (
            <div className="border-t px-1 pt-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full rounded px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted"
              >
                Išvalyti pasirinkimą
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
